import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Rocket, Check, ArrowRight, Circle, EyeOff, PlayCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useApp } from '../store/useApp';
import { registrarAuditoria } from '../lib/auditoria';

const PASOS = [
  { key: 'almacen', ruta: '/onboarding', video: '/tutoriales/monta-tu-almacen?t=40' },
  { key: 'etiquetas', ruta: '/etiquetas' },
  { key: 'inventario', ruta: '/inventario', video: '/tutoriales/monta-tu-almacen?t=225' },
  { key: 'pedido', ruta: '/pedidos/importar' },
  { key: 'buscar', ruta: '/buscar' },
];

const leer = (k, f) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? f; } catch { return f; } };

export function OnboardingGuia() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { tenantActivo } = useApp();
  const tid = tenantActivo?.tenant_id;
  const [estado, setEstado] = useState(null); // [bool x5]
  const [visible, setVisible] = useState(false);

  const evaluar = useCallback(async () => {
    if (!tid || !navigator.onLine) { setEstado(null); return; }
    const cuenta = async (tabla, extra) => {
      let q = supabase.from(tabla).select('*', { count: 'exact', head: true }).eq('tenant_id', tid);
      if (extra) q = extra(q);
      const { count } = await q;
      return count || 0;
    };
    const auditoriaTiene = async (patron) => {
      const { data } = await supabase.from('auditoria').select('id').eq('tenant_id', tid).or(patron).limit(1);
      return (data || []).length > 0;
    };
    const [nAlm, nUbi, nPedEtq, prog, hayImpr, hayConsulta] = await Promise.all([
      cuenta('almacenes'),
      cuenta('ubicaciones', (q) => q.eq('activo', true)),
      cuenta('pedidos', (q) => q.eq('estado', 'etiquetado')),
      supabase.from('v_inventario_progreso').select('comprobadas,inventario_cerrado_en').eq('tenant_id', tid).then(({ data }) => data || []),
      auditoriaTiene('operacion.ilike.EXPORT*,operacion.ilike.IMPRESION*'),
      auditoriaTiene('operacion.ilike.CONSULTA*'),
    ]);
    const invHecho = prog.some((r) => r.inventario_cerrado_en || Number(r.comprobadas) > 0);
    const st = [
      nAlm > 0 && nUbi > 0,   // 1 dar de alta
      hayImpr,                // 2 etiquetas
      invHecho,               // 3 inventario inicial
      nPedEtq > 0,            // 4 primer pedido con etiquetas
      hayConsulta,            // 5 encontrar un palet
    ];
    setEstado(st);

    // Registrar en auditoría cada paso recién completado (para saber dónde se atascan los trials).
    const rec = leer(`hueco-guia-rec-${tid}`, []);
    const nuevos = [];
    st.forEach((ok, i) => { if (ok && !rec.includes(i)) nuevos.push(i); });
    if (nuevos.length) {
      const todos = [...rec, ...nuevos];
      localStorage.setItem(`hueco-guia-rec-${tid}`, JSON.stringify(todos));
      nuevos.forEach((i) => registrarAuditoria(tid, `ONBOARDING_PASO_${i + 1}`, { paso: PASOS[i].key }));
    }

    // Visibilidad: se muestra si falta algún paso; si están los 5, solo reaparece al añadir un almacén nuevo.
    // "Ocultar por ahora" la esconde hasta que se añade un almacén nuevo (ahí vuelve a ser útil).
    const allDone = st.every(Boolean);
    const guardadoRaw = localStorage.getItem(`hueco-guia-alm-${tid}`);
    const almGuardado = guardadoRaw != null ? JSON.parse(guardadoRaw) : null;
    const nuevoAlmacen = almGuardado != null && nAlm > almGuardado;
    let oculta = localStorage.getItem(`hueco-guia-oculta-${tid}`) === 'true';
    if (nuevoAlmacen) { oculta = false; localStorage.removeItem(`hueco-guia-oculta-${tid}`); }
    setVisible((!allDone || nuevoAlmacen) && !oculta);
    if (allDone) localStorage.setItem(`hueco-guia-alm-${tid}`, JSON.stringify(nAlm));
  }, [tid]);

  useEffect(() => { setEstado(null); setVisible(false); evaluar(); }, [evaluar]);

  if (!estado || !visible) return null;
  const completados = estado.filter(Boolean).length;
  const actual = estado.findIndex((x) => !x); // primer paso pendiente
  const pct = Math.round((completados / PASOS.length) * 100);
  const ocultar = () => { localStorage.setItem(`hueco-guia-oculta-${tid}`, 'true'); setVisible(false); };

  return (
    <div className="card stack guia" data-testid="onboarding-guia">
      <div className="row spread wrap">
        <div className="row" style={{ gap: 10 }}><Rocket size={18} /><h2 style={{ margin: 0 }}>{t('guia.titulo')}</h2></div>
        <div className="row" style={{ gap: 8 }}>
          <span className="badge" data-testid="guia-progreso">{completados}/{PASOS.length}</span>
          <button className="btn ghost sm" onClick={ocultar} data-testid="guia-ocultar-btn"><EyeOff size={14} /> {t('guia.ocultar')}</button>
        </div>
      </div>
      <div className="guia-barra" aria-hidden><div className="guia-relleno" style={{ width: `${pct}%` }} data-testid="guia-barra" /></div>

      <ol className="guia-pasos">
        {PASOS.map((p, i) => {
          const hecho = estado[i];
          const esActual = i === actual;
          return (
            <li key={p.key} className={`guia-paso ${hecho ? 'hecho' : ''} ${esActual ? 'actual' : ''}`} data-testid={`guia-paso-${p.key}`}>
              <span className="guia-ico">{hecho ? <Check size={16} /> : <Circle size={16} />}</span>
              <span className="guia-txt">{t(`guia.paso.${p.key}`)}</span>
              {esActual && (
                <button className="btn sm primary" onClick={() => nav(p.ruta)} data-testid={`guia-ir-${p.key}`}>
                  {t(`guia.cta.${p.key}`)} <ArrowRight size={14} />
                </button>
              )}
              {esActual && p.video && (
                <button className="btn ghost sm" onClick={() => nav(p.video)} data-testid={`guia-video-${p.key}`}>
                  <PlayCircle size={14} /> {t('tutoriales.ver_como')}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
